import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getDefaultViewTypeIconKey } from 'utilities/ViewTypeIcons';
import { ViewType } from 'utilities/NodeType';

// CSS
import './CreateTypeSection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import RichSelect from 'components/richselect/RichSelect';
import TextInput from 'components/textinput/TextInput';
import TypeIcon from 'components/typeicon/TypeIcon';

/**
 * CreateTypeFields
 * 
 * This component renders the input fields for creating a new node type, including 
 * icon selection, label input, plural label input, description input, and default 
 * view selection.
 * 
 * @param {string} iconKey - The key of the currently selected icon for the type.
 * @param {function} setIconKey - Function to call when the icon selection changes.
 * @param {boolean} isSavingType - Whether the type is currently being saved, used to disable inputs.
 * @param {string} label - The current value of the label input field.
 * @param {function} setLabel - Function to call when the label input changes.
 * @param {string} labelHelperText - Helper text or error message to display below the label input.
 * @param {function} onSubmit - Function to call when the form is submitted (e.g., pressing Enter).
 * @param {string} labelPlural - The current value of the plural label input field.
 * @param {function} onLabelPluralChange - Function to call when the plural label input changes.
 * @param {string} pluralHelperText - Helper text or error message to display below the plural label input.
 * @param {string} description - Optional description for the node type.
 * @param {function} setDescription - Function to call when the description input changes.
 * @param {string} defaultView - The currently selected default view type.
 * @param {function} setDefaultView - Function to call when the default view selection changes.
 * @param {boolean} autoFocusLabel - Whether to auto-focus the label input on mount.
 * 
 * @returns {ReactNode} The rendered component.
 */
const CreateTypeFields = React.memo(({
  iconKey,
  setIconKey,
  isSavingType,
  label,
  setLabel,
  labelHelperText,
  onSubmit,
  labelPlural,
  onLabelPluralChange,
  pluralHelperText,
  description,
  setDescription,
  defaultView,
  setDefaultView,
  autoFocusLabel = false,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();

  // ----- STATE ----- //

  const [isEditingPlural, setIsEditingPlural] = useState(false);
  const [isPluralEditHovered, setIsPluralEditHovered] = useState(false);

  const canEditPlural = Boolean(String(label || '').trim());

  // Build options for default view selector with icons
  const defaultViewOptions = [
    { value: ViewType.TABLE, label: 'Table', iconKey: getDefaultViewTypeIconKey(ViewType.TABLE) },
    { value: ViewType.GALLERY, label: 'Gallery', iconKey: getDefaultViewTypeIconKey(ViewType.GALLERY) },
    { value: ViewType.LIST, label: 'List', iconKey: getDefaultViewTypeIconKey(ViewType.LIST) },
    { value: ViewType.FEED, label: 'Feed', iconKey: getDefaultViewTypeIconKey(ViewType.FEED) },
    { value: ViewType.CALENDAR, label: 'Calendar', iconKey: getDefaultViewTypeIconKey(ViewType.CALENDAR) },
    { value: ViewType.MAP, label: 'Map', iconKey: getDefaultViewTypeIconKey(ViewType.MAP) },
  ];

  return (
    
    <ModalForm>

      {/* ICON */}
      <ModalField>

        <ModalLabel label="Default Icon" />

        <div className="create-type-section-icon-row">
          <TypeIcon
            size={52}
            iconKey={iconKey}
            onChange={setIconKey}
            disabled={isSavingType}
          />
        </div>

        <ModalHint
          hint="Choose the default icon users will see for items in this database."
        />

      </ModalField>

      {/* SINGULAR NAME */}
      <ModalField>

        {/* LABEL */}
        <ModalLabel label="Singular Name" />

        {/* INPUT */}
        <TextInput
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. 'Contact'"
          maxLength={80}
          autoFocus={autoFocusLabel}
          error={Boolean(labelHelperText)}
          helperText={labelHelperText}
          onEnterPress={onSubmit}
        />

        <ModalHint
          hint="For individual items in this database."
        />

      </ModalField>

      {/* PLURAL NAME */}
      <ModalField>

        {/* LABEL */}
        <ModalLabel label="Plural Name" />

        {/* INPUT */}
        {isEditingPlural ? (

          <TextInput
            value={labelPlural}
            onChange={(e) => onLabelPluralChange(e.target.value)}
            onBlur={() => setIsEditingPlural(false)}
            placeholder="e.g. 'Tasks'"
            maxLength={80}
            error={Boolean(pluralHelperText)}
            helperText={pluralHelperText}
            onEnterPress={onSubmit}
            autoFocus
          />

        ) : (

          <div
            className={`create-type-section-plural-readonly-row ${canEditPlural ? '' : 'no-edit'}`.trim()}
            style={{
              backgroundColor: theme.backgroundMuted,
            }}
          >
            <div className="create-type-section-plural-readonly-value"
              style={{
                color: theme.foreground
              }}>
              {labelPlural || 'Not set'}
            </div>

            {canEditPlural && (

              <div className="create-type-section-plural-edit-button"
                onClick={() => setIsEditingPlural(true)}
                onMouseEnter={() => setIsPluralEditHovered(true)}
                onMouseLeave={() => setIsPluralEditHovered(false)}
                style={{
                  backgroundColor: isPluralEditHovered ? theme.backgroundHover : theme.backgroundMuted,
                  border: `1px solid ${isPluralEditHovered ? theme.foregroundMuted : 'transparent'}`,
                }}
                aria-label="Edit plural name"
                title="Edit plural name"
              >
                <Icon
                  iconKey="edit"
                  width={20}
                  height={20}
                  color={theme.foregroundMuted}
                />
              </div>

            )}

          </div>

        )}

        <ModalHint
          hint="For groups of items from this database."
        />

      </ModalField>

      {/* DESCRIPTION */}
      <ModalField>

        {/* LABEL */}
        <ModalLabel label="Description" />

        {/* DESCRIPTION */}
        <TextInput
          value={description || ''}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional short description for this type"
          maxLength={110}
          lines={2}
          disabled={isSavingType}
          multiline
          expandable
          onEnterPress={onSubmit}
        />

        <ModalHint
          hint="A short explanation can help members choose the right database or item type."
        />

      </ModalField>

      {/* DEFAULT VIEW */}
      <ModalField>

        {/* LABEL */}
        <ModalLabel label="Default View" />

        {/* INPUT */}
        <RichSelect
          value={defaultView || ''}
          options={defaultViewOptions}
          placeholder="Select default view"
          onValueChange={(nextValue) => {
            if (typeof setDefaultView === 'function') {
              setDefaultView(nextValue);
            }
          }}
          style={{ width: '100%' }}
          disabled={isSavingType || typeof setDefaultView !== 'function'}
        />

        <ModalHint
          hint="When space members first view items in this database, they'll see this view by default."
        />

      </ModalField>

    </ModalForm>
  );
});

CreateTypeFields.displayName = 'CreateTypeFields';

export default CreateTypeFields;
